import { styled } from "styled-components";
import { Form, Input, Select, UploadFile } from "antd";
import { BusinessProfile } from "../../../models/business-profile.model";
import { useForm } from "antd/es/form/Form";
import {
  MdCheck,
  MdCheckCircleOutline,
  MdLink,
  MdRemove,
  MdShare,
} from "react-icons/md";
import { colors } from "../../../theme/colors";
import { useCallback, useEffect, useState } from "react";
import { debounceTime, Subject } from "rxjs";
import PhoneInput from "antd-phone-input";
import { validateUrl } from "../../../utils/functions/string/validateUrl";
import { Splash } from "../../../components/ui/splash/splash.component";
import { useNavigate, useSearchParams } from "react-router-dom";
import { wait } from "../../../utils/functions/misc/wait";
import { SubmitButton } from "../../../components/layout/submit-button/submit-button.component";
import { ImageUploader } from "../../../components/ui/image-uploader/image-uploader.component";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import { AxiosError } from "axios";
import toast from "react-hot-toast";
import { useAtomValue } from "jotai";
import { masterAtom } from "../../../atoms/masters.atom";
import { masterToOptions } from "../../../utils/functions/misc/masterToOptions";
import { axios } from "../../../api/axios";
import { Pressable } from "../../../components/ui/pressable/pressable.component";
import { TextAreaResize } from "../../../components/ui/textarea-resize/textarea-resize.component";
import {
  CUSTOM_URL_MAX_LENGTH,
  isInvalidCustomUrl,
  sanitizeCustomUrl,
} from "../../../utils/functions/string/custom-url";
import { sluggify } from "../../../utils/functions/string/sluggify";
import { Page } from "../../../components/layout/page/page.component";
import { Card } from "../../../components/ui/card/card.component";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaWhatsapp,
  FaXTwitter,
} from "react-icons/fa6";

const customUrlSubject$ = new Subject<string>();

type SocialPlatform = "facebook" | "linkedin" | "instagram" | "whatsapp" | "x";

const SOCIAL_FIELDS: {
  platform: SocialPlatform;
  urlKey: `${SocialPlatform}_url`;
  label: string;
  prefix: string;
  placeholder: string;
  icon: JSX.Element;
}[] = [
  {
    platform: "facebook",
    urlKey: "facebook_url",
    label: "Facebook",
    prefix: "https://facebook.com/",
    placeholder: "your.page",
    icon: <FaFacebook color="#1877F2" size={20} />,
  },
  {
    platform: "linkedin",
    urlKey: "linkedin_url",
    label: "LinkedIn",
    prefix: "https://linkedin.com/company/",
    placeholder: "your-company",
    icon: <FaLinkedin color="#0A66C2" size={20} />,
  },
  {
    platform: "instagram",
    urlKey: "instagram_url",
    label: "Instagram",
    prefix: "https://instagram.com/",
    placeholder: "your.handle",
    icon: <FaInstagram color="#E1306C" size={20} />,
  },
  {
    platform: "whatsapp",
    urlKey: "whatsapp_url",
    label: "WhatsApp",
    prefix: "https://wa.me/",
    placeholder: "XXYYYYYYYYYY",
    icon: <FaWhatsapp color="#25D366" size={20} />,
  },
  {
    platform: "x",
    urlKey: "x_url",
    label: "X",
    prefix: "https://x.com/",
    placeholder: "your-handle",
    icon: <FaXTwitter size={20} />,
  },
];

export const CreateBusinessProfile = ({ editing }: { editing?: boolean }) => {
  const navigate = useNavigate();

  const master = useAtomValue(masterAtom);

  const [searchParams] = useSearchParams();

  const [showSuccess, setShowSuccess] = useState(false);

  const [primaryBusinessActivity, setPrimaryBusinessActivity] = useState<
    string | null
  >(null);
  const [logo, setLogo] = useState<File | null>(null);
  const [logoURL, setLogoURL] = useState<string | null>(null);

  const [urlVerified, setUrlVerified] = useState<boolean | null>(null);
  const [showVerification, setShowVerification] = useState(true);
  const [customUrl, setCustomUrl] = useState("");

  const [about, setAbout] = useState("");

  const [form] = useForm<BusinessProfile>();

  const onSubmit = useCallback(async () => {
    try {
      await form.validateFields();

      const profile = { ...form.getFieldsValue() } as BusinessProfile &
        Record<string, any>;

      for (const { urlKey, prefix } of SOCIAL_FIELDS) {
        const v = profile[urlKey] as string | undefined;
        if (v && v.trim()) {
          profile[urlKey] = v.startsWith("http") ? v : `${prefix}${v}`;
        } else {
          delete profile[urlKey];
        }
      }

      if (profile.mobile?.secondary?.phoneNumber) {
        profile.secondary_phone_code =
          profile.mobile.secondary.countryCode.toString();
        profile.secondary_phone_number =
          profile.mobile.secondary.areaCode +
          profile.mobile.secondary.phoneNumber;
      }
      if (profile.mobile?.primary) {
        profile.primary_phone_code =
          profile.mobile.primary.countryCode.toString();
        profile.primary_phone_number =
          profile.mobile.primary.areaCode + profile.mobile.primary.phoneNumber;
      }
      delete profile["mobile"];
      delete profile["primary_phone"];
      delete profile["secondary_phone"];

      if (!profile.business_name) {
        throw new Error();
      }
      if (about) {
        profile.about = about;
      }
      profile.other_activities =
        profile.other_activities?.filter((el) => !!el) ?? [];
      if (!profile.company_type) delete profile["company_type"];
      profile.custom_username = profile.custom_url ?? "company-name";
      if (profile.website_url && !profile.website_url.startsWith("https://"))
        profile.website_url = `https://${profile.website_url}`;

      const newProfileForm = new FormData();
      for (const key in profile) {
        if (key === "logo") continue;

        const value = profile[key as keyof typeof profile]!;
        if (Array.isArray(value)) {
          for (let i = 0; i < value.length; i++) {
            newProfileForm.append(
              key,
              (typeof value[i] === "string"
                ? value[i]
                : JSON.stringify(value[i])) as any
            );
          }
        } else if (!!value) {
          newProfileForm.append(key, value as string);
        }
      }
      if (logo) {
        newProfileForm.append("logo", logo);
      } else if (logoURL) {
        newProfileForm.append("logo", logoURL);
      }

      (editing ? axios.put : axios.post)(
        "/auth/business/profile",
        newProfileForm,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
        .then((resp) =>
          handleResponse(resp, async () => {
            setShowSuccess(true);
            await wait(3000);
            navigate(searchParams.get("r") ?? "/business", { replace: true });
          })
        )
        .catch((error) => {
          if (error instanceof AxiosError) {
            let errorFields = error.response?.data ?? {};
            if (errorFields.message) errorFields = errorFields.message;

            let hasSetError = false;
            for (const key in errorFields) {
              if (form.getFieldInstance(key)) hasSetError = true;

              if (form.getFieldInstance(key))
                form.setFields([
                  {
                    name: key,
                    errors: Array.isArray(errorFields[key])
                      ? errorFields[key]
                      : typeof errorFields[key] === "object"
                      ? [errorFields[key]["0"] ?? ""]
                      : [errorFields[key]],
                  },
                ]);
            }

            if (!hasSetError) {
              const message = Object.entries(errorFields)
                .map(([key, errors]: any) => `${key}: ${errors.join(",")}`)
                .join(";");

              toast.error(message);
            }
          } else {
            handleResponse(error);
          }
        });
    } catch (e) {
      if (e instanceof AxiosError) {
        handleResponse(e);
      } else {
        toast.error("Please fix the errors in the form!");
      }
    }
  }, [form, logo, logoURL, editing, about, searchParams]);

  useEffect(() => {
    axios
      .get<{
        profile: BusinessProfile;
      }>("/auth/business/profile")
      .then(async (result) => {
        !editing && navigate("/b");
        if (editing) {
          const profile = {
            ...result.data.profile,
          } as BusinessProfile & Record<string, any>;
          const pendingChanges = profile.pending_changes;
          delete (profile as any).pending_changes;

          for (const pendingChange of pendingChanges) {
            profile[pendingChange.field_name as any as keyof typeof profile] =
              pendingChange.proposed_value;
          }

          for (const { urlKey, prefix } of SOCIAL_FIELDS) {
            const v = profile[urlKey];
            if (v && typeof v === "string") {
              profile[urlKey] = v.replace(prefix, "");
            }
          }

          profile.custom_url = profile.custom_username;
          delete profile["custom_username"];

          profile.primary_phone = {
            countryCode: +profile.primary_phone_code,
            phoneNumber: profile.primary_phone_number,
            areaCode: "",
          };
          delete (profile as any).primary_phone_code;
          delete (profile as any).primary_phone_number;

          if (profile.secondary_phone_code && profile.secondary_phone_number) {
            profile.secondary_phone = {
              countryCode: +profile.secondary_phone_code,
              phoneNumber: profile.secondary_phone_number,
              areaCode: "",
            };
            delete profile.secondary_phone_code;
            delete profile.secondary_phone_number;
          }
          if (profile.logo) {
            setLogoURL(profile.logo);
          }
          if (profile.website_url) {
            profile.website_url = profile.website_url.replace("https://", "");
          }
          if (profile.about) setAbout(profile.about);
          if (!profile.about) delete profile.about;
          if (profile.custom_url) setCustomUrl(profile.custom_url);

          form.setFieldsValue(profile);
          form.setFieldValue(["mobile", "primary"], profile.primary_phone);
          if (profile.secondary_phone) {
            form.setFieldValue(
              ["mobile", "secondary"],
              profile.secondary_phone
            );
          }

          for (const pendingChange of pendingChanges) {
            form.setFields([
              {
                name: pendingChange.field_name,
                warnings: [
                  "This change is waiting for approval and will reflect once approved!",
                ],
              },
            ]);
          }
        }
      })
      .catch((e) => {
        if (e instanceof AxiosError && e.response?.status === 404)
          editing && navigate("/p", { replace: true });
        else {
          handleResponse(e);
        }
      });
  }, [form, editing]);

  useEffect(() => {
    const listener = customUrlSubject$
      .pipe(debounceTime(500))
      .subscribe((value) => {
        setCustomUrl(value);
      });

    return () => listener.unsubscribe();
  }, []);

  useEffect(() => {
    if (!customUrl) return;
    setShowVerification(false);

    axios
      .get(`/client/validate/custom-url?q=${customUrl}&category=business`)
      .then(() => setUrlVerified(true))
      .catch(() => setUrlVerified(false))
      .finally(() => setShowVerification(true));
  }, [customUrl]);

  const shareProfile = async () => {
    const url = `https://distribute.pocketfilms.in/${
      form.getFieldValue("custom_url") ?? ""
    }`;

    if (
      (window.navigator as any).share &&
      window.navigator.canShare({ url })
    ) {
      try {
        await navigator.share({ url });
      } catch (e) {}
    } else {
      toast.success("Copied to clipboard!");
      window.navigator.clipboard.writeText(url);
    }
  };

  return (
    <Page>
      <Splash
        show={showSuccess}
        title={`Business profile ${
          editing ? "saved" : "created"
        } successfully!`}
        icon={<MdCheckCircleOutline />}
      />
      <Wrap>
        <Form form={form} layout="vertical" requiredMark>
          <PageHeader>
            <PageTitle>
              {editing
                ? "Edit your business profile"
                : "Create your business profile"}
            </PageTitle>
            <PageSubtitle>
              Tell filmmakers and partners who your company is and what you do.
            </PageSubtitle>
          </PageHeader>

          <CardGrid>
            <SectionCard>
              <SectionHeader>
                <SectionTitleBlock>
                  <SectionTitle>About Company</SectionTitle>
                  <SectionHint>
                    Your logo, public URL and a short company bio.
                  </SectionHint>
                </SectionTitleBlock>
              </SectionHeader>

              <UrlFieldLabel>Public profile URL</UrlFieldLabel>
              <UrlControl>
                <UrlVerified
                  $verified={urlVerified}
                  $show={showVerification}
                  title={
                    urlVerified === null
                      ? "Checking..."
                      : urlVerified
                      ? "URL is available"
                      : "URL is taken"
                  }
                >
                  {urlVerified === null ? (
                    <MdLink color={colors.white.hex} size={16} />
                  ) : urlVerified ? (
                    <MdCheck color={colors.white.hex} size={16} />
                  ) : (
                    <MdRemove color={colors.white.hex} size={16} />
                  )}
                </UrlVerified>
                <UrlInputGroup>
                  <UrlPrefix>distribute.pocketfilms.in/</UrlPrefix>
                  <Form.Item
                    name="custom_url"
                    style={{ marginBottom: 0, flex: 1 }}
                    rules={[
                      {
                        validator: (_, value) => {
                          const v = `${value ?? ""}`;
                          if (!v.trim()) {
                            return Promise.reject(
                              new Error("Please enter a custom URL!")
                            );
                          }
                          if (v.length > CUSTOM_URL_MAX_LENGTH) {
                            return Promise.reject(
                              new Error(
                                `Max. limit is ${CUSTOM_URL_MAX_LENGTH} characters`
                              )
                            );
                          }
                          if (isInvalidCustomUrl(v)) {
                            return Promise.reject(
                              new Error(
                                "Only letters and numbers are allowed."
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <UrlInput
                      autoComplete="off"
                      name="custom_url"
                      maxLength={CUSTOM_URL_MAX_LENGTH}
                      placeholder="your-company"
                      onInput={(e) => {
                        const v = sanitizeCustomUrl(
                          (e.target as HTMLInputElement).value
                        );
                        (e.target as HTMLInputElement).value = v;
                        form.setFieldValue("custom_url", v);
                        customUrlSubject$.next(v);
                        setCustomUrl(v);
                      }}
                    />
                  </Form.Item>
                </UrlInputGroup>
                <Pressable onClick={shareProfile}>
                  <ShareButton title="Share">
                    <MdShare color={colors.white.hex} size={16} />
                  </ShareButton>
                </Pressable>
              </UrlControl>

              <ProfileBody>
                <AvatarColumn>
                  <AvatarLabel>Logo</AvatarLabel>
                  <FormItem name="logo" $noLabel>
                    <ImageUploader
                      name="logo"
                      url={logoURL ? [logoURL] : []}
                      action=""
                      onRemove={() => {
                        setLogoURL(null);
                        setLogo(null);
                      }}
                      onComplete={(url: UploadFile[]) => {
                        setLogoURL(url[0].response[0]);
                        setLogo(url[0].response[1]);
                      }}
                    />
                  </FormItem>
                </AvatarColumn>
                <ProfileFields>
                  <Grid>
                    <FormItem
                      $span={2}
                      label="Company Name"
                      name="business_name"
                      required
                      rules={[
                        {
                          required: true,
                          message: "Please enter company name!",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter company name"
                        onInput={(e) => {
                          if (form.isFieldTouched("custom_url")) return;
                          const v = sluggify(
                            (e.target as HTMLInputElement).value,
                            ""
                          );
                          form.setFieldValue("custom_url", v);
                          customUrlSubject$.next(v);
                          setCustomUrl(v);
                        }}
                      />
                    </FormItem>
                    <FormItem
                      $span={2}
                      label="Doing Business As"
                      name="doing_business_as"
                    >
                      <Input placeholder="Enter brand name" />
                    </FormItem>
                  </Grid>
                  <FormItem label="About Company" name="about">
                    <TextAreaResize
                      name="about"
                      placeholder="Describe the company"
                      rows={5}
                      value={about}
                      onChange={setAbout}
                    />
                  </FormItem>
                </ProfileFields>
              </ProfileBody>
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <SectionTitleBlock>
                  <SectionTitle>Business Activities</SectionTitle>
                  <SectionHint>
                    What your company does and how it's structured.
                  </SectionHint>
                </SectionTitleBlock>
              </SectionHeader>

              <Grid>
                <FormItem
                  $span={2}
                  label="Primary Business Activity"
                  name="primary_activity"
                  required
                  rules={[
                    {
                      required: true,
                      message: "Please select primary business activity!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    options={masterToOptions("business", master)}
                    onSelect={(value) => setPrimaryBusinessActivity(value)}
                    placeholder="Select primary business activity"
                  />
                </FormItem>
                <FormItem
                  $span={2}
                  label="Other Business Activities"
                  name="other_activities"
                >
                  <Select
                    showSearch
                    mode="multiple"
                    options={masterToOptions(
                      "business",
                      master,
                      primaryBusinessActivity ? [primaryBusinessActivity] : []
                    )}
                    placeholder="Select other business activities"
                  />
                </FormItem>
                <FormItem label="Company Type" name="company_type">
                  <Select
                    showSearch
                    options={masterToOptions("company_types", master)}
                    placeholder="Select company type"
                  />
                </FormItem>
                <FormItem
                  label="Company Website URL"
                  name="website_url"
                  rules={[
                    {
                      validator: (_, value) =>
                        !value || validateUrl(value)
                          ? Promise.resolve()
                          : Promise.reject(),
                      message: "Please enter a valid URL!",
                    },
                  ]}
                >
                  <Input
                    prefix="https://"
                    placeholder="example.com"
                  />
                </FormItem>
              </Grid>
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <SectionTitleBlock>
                  <SectionTitle>Contact Information</SectionTitle>
                  <SectionHint>
                    How filmmakers and partners can reach the company.
                  </SectionHint>
                </SectionTitleBlock>
              </SectionHeader>

              <Grid>
                <FormItem
                  $span={2}
                  label="Company Email"
                  name="business_email"
                  rules={[
                    { required: true, message: "Please enter email address!" },
                    { type: "email", message: "Please enter a valid email!" },
                  ]}
                  required
                >
                  <Input
                    name="business_email"
                    type="email"
                    placeholder="Enter company email"
                  />
                </FormItem>
                <FormItem
                  label="Primary Contact Number"
                  name={["mobile", "primary"]}
                  rules={[
                    {
                      required: true,
                      message: "Please enter a primary phone number!",
                    },
                  ]}
                  required
                >
                  <PhoneInput
                    country="in"
                    disableParentheses
                    enableSearch
                    enableArrow
                    name="primary_mobile"
                    placeholder="Enter primary contact number"
                  />
                </FormItem>
                <FormItem
                  label="Company Mobile Number"
                  name={["mobile", "secondary"]}
                >
                  <PhoneInput
                    country="in"
                    disableParentheses
                    enableSearch
                    enableArrow
                    name="secondary_mobile"
                    placeholder="Enter company mobile number"
                  />
                </FormItem>
              </Grid>
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <SectionTitleBlock>
                  <SectionTitle>Social Media Links</SectionTitle>
                  <SectionHint>
                    Optional. Add the platforms your company is on.
                  </SectionHint>
                </SectionTitleBlock>
              </SectionHeader>

              <SocialList>
                {SOCIAL_FIELDS.map(
                  ({ platform, urlKey, label, prefix, placeholder, icon }) => (
                    <SocialRow key={platform}>
                      <SocialBadge>
                        {icon}
                        <SocialLabel>{label}</SocialLabel>
                      </SocialBadge>
                      <FormItem
                        $noLabel
                        name={urlKey}
                        style={{ marginBottom: 0, flex: 1 }}
                      >
                        <Input
                          placeholder={placeholder}
                          prefix={<SocialPrefix>{prefix}</SocialPrefix>}
                        />
                      </FormItem>
                    </SocialRow>
                  )
                )}
              </SocialList>
            </SectionCard>
          </CardGrid>
        </Form>
      </Wrap>
      <SubmitButton
        label={editing ? "Save" : "Create"}
        onClick={() => {
          onSubmit();
        }}
      />
    </Page>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  padding: 0 0 12rem 0;

  .ant-form-item-has-error,
  .ant-form-item-has-warning {
    margin-bottom: 3rem;
  }
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
  margin-bottom: 0.4rem;
`;

const PageTitle = styled.h1`
  font-size: 2.4rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
  line-height: 1.2;
`;

const PageSubtitle = styled.p`
  font-size: 1.4rem;
  color: ${colors.text.withOpacity(0.6).hex};
  margin: 0;
  line-height: 1.4;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem;
  width: 100%;
  align-items: stretch;

  @media (max-width: 60em) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SectionCard = styled(Card)`
  padding: 2rem 2.4rem;
  border: 1.5px solid ${colors.primary.withOpacity(0.3).hex};
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  margin-bottom: 1.6rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${colors.grey.hex};
`;

const SectionTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
  flex: 1;
`;

const SectionTitle = styled.h3`
  font-size: 1.7rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
`;

const SectionHint = styled.span`
  font-size: 1.25rem;
  color: ${colors.text.withOpacity(0.6).hex};
  line-height: 1.4;
`;

const UrlFieldLabel = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors.text.withOpacity(0.65).hex};
  margin-bottom: 0.6rem;
`;

const UrlControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  margin-bottom: 2rem;
`;

const UrlVerified = styled.div<{
  $verified?: boolean | null;
  $show?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  flex-shrink: 0;
  opacity: ${({ $show }) => ($show ? 1 : 0.4)};
  background-color: ${({ $verified }) =>
    $verified === null
      ? colors.primary.hex
      : $verified
      ? colors.success.hex
      : colors.error.hex};
  transition: all 0.3s;
`;

const UrlInputGroup = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  border: 1.5px solid ${colors.primary.withOpacity(0.4).hex};
  border-radius: 0.8rem;
  overflow: hidden;
  background: ${colors.background.hex};
  height: 3.6rem;

  &:focus-within {
    border-color: ${colors.primary.hex};
    box-shadow: 0 0 0 3px ${colors.primary.withOpacity(0.15).hex};
  }
`;

const UrlPrefix = styled.span`
  display: flex;
  align-items: center;
  padding: 0 0.8rem 0 1rem;
  font-size: 1.2rem;
  color: ${colors.text.withOpacity(0.6).hex};
  border-right: 1px solid ${colors.grey.hex};
  white-space: nowrap;

  @media (max-width: 75em) {
    display: none;
  }
`;

const UrlInput = styled.input.attrs({ type: "text" })`
  border: none;
  outline: none;
  background: transparent;
  font-size: 1.35rem;
  font-weight: 500;
  color: ${colors.text.hex};
  padding: 0 1rem;
  width: 100%;
  min-width: 0;
`;

const ShareButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 3.2rem;
  width: 3.2rem;
  border-radius: 50%;
  background: ${colors.primary.hex};
  color: ${colors.white.hex};
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: ${colors.primary.withOpacity(0.9).hex};
  }
`;

const ProfileBody = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 2rem;
  width: 100%;
  align-items: flex-start;

  @media (max-width: 36em) {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.4rem;
  }
`;

const AvatarColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-upload-list-picture-card .ant-upload-list-item-container,
  .ant-upload-select,
  .avatar-uploader > .ant-upload {
    width: 9rem !important;
    height: 9rem !important;
  }

  @media (max-width: 36em) {
    align-items: flex-start;
  }
`;

const AvatarLabel = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors.text.withOpacity(0.65).hex};
`;

const ProfileFields = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;

const SocialList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  width: 100%;
`;

const SocialRow = styled.div`
  display: grid;
  grid-template-columns: 13rem minmax(0, 1fr);
  gap: 1rem;
  align-items: center;
  width: 100%;

  .ant-form-item {
    margin-bottom: 0;
  }

  @media (max-width: 36em) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.6rem;
  }
`;

const SocialBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.6rem 1rem;
  border-radius: 0.8rem;
  background: ${colors.lightBackground.hex};
  border: 1.5px solid ${colors.primary.withOpacity(0.2).hex};
  height: 3.6rem;
`;

const SocialLabel = styled.span`
  font-size: 1.35rem;
  font-weight: 500;
  color: ${colors.text.hex};
`;

const SocialPrefix = styled.span`
  font-size: 1.2rem;
  color: ${colors.text.withOpacity(0.55).hex};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 2rem;
  row-gap: 0;
  width: 100%;

  @media (max-width: 50em) {
    grid-template-columns: minmax(0, 1fr);
    column-gap: 0;
  }
`;

const FormItem = styled(Form.Item)<{
  $span?: 1 | 2;
  $noLabel?: boolean;
  $disabled?: boolean;
}>`
  width: 100%;
  margin-bottom: 2rem;
  grid-column: ${({ $span }) => ($span === 2 ? "1 / -1" : "auto")};

  ${({ $noLabel }) =>
    $noLabel ? `.ant-form-item-label { display: none; }` : ""}

  ${({ $disabled }) => ($disabled ? "opacity: 0.6; pointer-events: none;" : "")}

  @media (max-width: 50em) {
    grid-column: 1 / -1;
  }

  .ant-form-item-label {
    padding-bottom: 0.4rem;

    > label {
      font-size: 1.4rem;
      height: auto;
      line-height: 1.4;
    }
  }

  .ant-form-item-explain,
  .ant-form-item-extra {
    font-size: 1.2rem;
    min-height: auto;
    line-height: 1.4;
    padding-top: 0.4rem;
  }

  .ant-input,
  .ant-select-selector,
  .ant-picker {
    font-size: 1.4rem;
  }
`;
